<template>
  <NuxtCarousel
    ref="slider"
    class="basic-slider"
    v-bind="configOverride"
    @init="initCarousel"
  >
    <NuxtSlide
      v-for="slide in slides"
      :key="slide.id"
    >
      <slot :slide="slide" />
    </NuxtSlide>
    <template #addons>
      <NuxtNavigation />
    </template>
  </NuxtCarousel>
</template>

<script setup lang="ts" generic="T extends Slide">
import type { Slide } from '#shared/types/Slider';
import { useMutationObserver } from '@vueuse/core';
import { type CarouselConfig, CarouselMode } from '~~/models/NuxtCarousel';

interface Props {
  slides: Array<T>;
  config?: Partial<CarouselConfig>;
}

const props = withDefaults(defineProps<Props>(), {
  config: undefined,
});

const configOverride: ComputedRef<Partial<CarouselConfig>> = computed(() => {
  return Object.assign(defaultConfigOverride, props.config);
});

// All defaults can be found here: https://vue3-carousel.ismail9k.com/config.html
const defaultConfigOverride: Partial<CarouselConfig> = {
  itemsToShow: 1.5,
  wrapAround: true,
  breakpointMode: CarouselMode.CAROUSEL,
};

const slider = useTemplateRef('slider');

const setTabIndexAndInert = (element: Node, tabIndex: number, inert: boolean): void => {
  if (element instanceof HTMLElement) {
    element.tabIndex = tabIndex;
    element.inert = inert;
    const children = element.querySelectorAll('a');
    for (const child of children) {
      if (child instanceof HTMLElement) {
        child.tabIndex = tabIndex;
      }
    }
  }
};

const initCarousel = () => {
  const hiddenSlides = slider.value?.$el?.querySelectorAll('.carousel__slide') ?? [];
  for (const slide of hiddenSlides) {
    if (slide.classList?.contains('carousel__slide--visible')) {
      setTabIndexAndInert(slide, 0, false);
    } else {
      setTabIndexAndInert(slide, -1, true);
    }
  }

  useMutationObserver(
    slider,
    (mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (
            target.classList?.contains('carousel__slide')
            && target.classList?.contains('carousel__slide--visible')
          ) {
            setTabIndexAndInert(target, 0, false);
          } else if (
            target.classList?.contains('carousel__slide')
            && !target.classList?.contains('carousel__slide--visible')
          ) {
            setTabIndexAndInert(target, -1, true);
          }
        }
      }
    },
    {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    },
  );
};
</script>